import React, { useMemo } from "react";
import { cn } from "../../utils/cn";

const Input = React.forwardRef(
    (
        {
            className,
            type = "text",
            label,
            description,
            error,
            required = false,
            id,
            onKeyDown,
            onBlur,
            // ✅ opcional: si quieres que al presionar Enter se cierre teclado
            blurOnEnter = true,
            ...props
        },
        ref
    ) => {
        // ✅ ID estable (evita mismatch SSR y re-render raro)
        const inputId = useMemo(() => {
            return id || `input-${Math.random().toString(36).slice(2, 11)}`;
        }, [id]);

        // ✅ FIX iOS zoom:
        // - En mobile: text-base (16px)
        // - sm+: text-sm
        const baseInputClasses =
            "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 " +
            "text-base sm:text-sm ring-offset-background " +
            "file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground " +
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 " +
            "disabled:cursor-not-allowed disabled:opacity-50";

        // Checkbox
        if (type === "checkbox") {
            return (
                <input
                    type="checkbox"
                    className={cn(
                        "h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                        className
                    )}
                    ref={ref}
                    id={inputId}
                    {...props}
                />
            );
        }

        // Radio
        if (type === "radio") {
            return (
                <input
                    type="radio"
                    className={cn(
                        "h-4 w-4 rounded-full border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                        className
                    )}
                    ref={ref}
                    id={inputId}
                    {...props}
                />
            );
        }

        const handleKeyDown = (e) => {
            // ✅ Enter => blur (cierra teclado en mobile)
            if (blurOnEnter && e.key === "Enter") {
                e.currentTarget.blur();
            }
            onKeyDown?.(e);
        };

        const handleBlur = (e) => {
            onBlur?.(e);
        };

        // ✅ inputMode por tipo (mejor teclado iOS/Android)
        const inferredInputMode =
            props.inputMode ||
            (type === "number" ? "numeric" : type === "email" ? "email" : type === "tel" ? "tel" : undefined);

        return (
            <div className="space-y-2">
                {label && (
                    <label
                        htmlFor={inputId}
                        className={cn(
                            "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
                            error ? "text-destructive" : "text-foreground"
                        )}
                    >
                        {label}
                        {required && <span className="text-destructive ml-1">*</span>}
                    </label>
                )}

                <input
                    type={type}
                    className={cn(baseInputClasses, error && "border-destructive focus-visible:ring-destructive", className)}
                    ref={ref}
                    id={inputId}
                    inputMode={inferredInputMode}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    {...props}
                />

                {description && !error && <p className="text-sm text-muted-foreground">{description}</p>}
                {error && <p className="text-sm text-destructive">{error}</p>}
            </div>
        );
    }
);

Input.displayName = "Input";

export default Input;
